import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, CheckCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgendarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedRoom = searchParams.get('sala');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    roomId: '',
    meetingName: '',
    responsible: '',
    date: '',
    startTime: '',
    endTime: '',
    recurrence: 'none',
    recurrenceEndDate: ''
  });

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/public/rooms`);
      setRooms(response.data);
      // Use pre-selected room from query parameter, or first room
      const defaultRoom = preSelectedRoom && response.data.find(r => r.id === preSelectedRoom)
        ? preSelectedRoom
        : response.data[0]?.id || '';
      setFormData(prev => ({ ...prev, roomId: defaultRoom }));
    } catch (error) {
      toast.error('Erro ao carregar salas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDateTime = `${formData.date}T${formData.startTime}:00-03:00`;
      const endDateTime = `${formData.date}T${formData.endTime}:00-03:00`;

      const payload = {
        room_id: formData.roomId,
        start_time: startDateTime,
        end_time: endDateTime,
        meeting_name: formData.meetingName,
        responsible: formData.responsible
      };

      if (formData.recurrence !== 'none') {
        payload.recurrence = formData.recurrence;
        payload.recurrence_end_date = `${formData.recurrenceEndDate}T23:59:59-03:00`;
      }

      const response = await axios.post(`${API}/public/bookings`, payload);
      const count = response.data.count || 1;
      
      setSuccess(true);
      toast.success(`${count} reunião(ões) agendada(s) com sucesso!`);
      
      // Reset form
      setFormData({
        roomId: rooms[0]?.id || '',
        meetingName: '',
        responsible: '',
        date: '',
        startTime: '',
        endTime: '',
        recurrence: 'none',
        recurrenceEndDate: ''
      });

      // Reset success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao agendar reunião');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10">
      {/* Header */}
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-manrope font-bold tracking-tight">RoomBook</h1>
              <p className="text-sm text-muted-foreground font-inter">Sistema de Agendamento</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        {success ? (
          <div className="bg-white rounded-3xl border-2 border-secondary p-12 shadow-2xl text-center animate-in fade-in zoom-in duration-500">
            <div className="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-secondary" />
            </div>
            <h2 className="text-4xl font-manrope font-bold tracking-tight mb-4 text-secondary">
              Reunião Agendada!
            </h2>
            <p className="text-lg text-muted-foreground font-inter mb-8">
              Sua reunião foi agendada com sucesso.
            </p>
            <Button
              onClick={() => setSuccess(false)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-8 font-semibold"
            >
              Agendar Outra Reunião
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border-2 border-border/50 p-8 md:p-12 shadow-2xl">
            <div className="mb-8">
              <h2 className="text-4xl md:text-5xl font-manrope font-bold tracking-tight mb-3">
                Agendar Reunião
              </h2>
              <p className="text-lg text-muted-foreground font-inter">
                Preencha os dados abaixo para reservar uma sala
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sala */}
              <div className="space-y-2">
                <Label htmlFor="roomId" className="text-sm font-medium">
                  Sala de Reunião *
                </Label>
                <select
                  id="roomId"
                  data-testid="agendar-room-select"
                  value={formData.roomId}
                  onChange={(e) => setFormData({ ...formData, roomId: e.target.value })}
                  required
                  className="w-full h-12 bg-white/50 backdrop-blur-sm border border-border/50 rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name} - {room.capacity} pessoas
                    </option>
                  ))}
                </select>
              </div>

              {/* Nome da Reunião */}
              <div className="space-y-2">
                <Label htmlFor="meetingName" className="text-sm font-medium">
                  Nome da Reunião *
                </Label>
                <Input
                  id="meetingName"
                  data-testid="agendar-meeting-name-input"
                  type="text"
                  value={formData.meetingName}
                  onChange={(e) => setFormData({ ...formData, meetingName: e.target.value })}
                  placeholder="Ex: Reunião de Planejamento Trimestral"
                  required
                  className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                />
              </div>

              {/* Responsável */}
              <div className="space-y-2">
                <Label htmlFor="responsible" className="text-sm font-medium">
                  Responsável *
                </Label>
                <Input
                  id="responsible"
                  data-testid="agendar-responsible-input"
                  type="text"
                  value={formData.responsible}
                  onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                  placeholder="Nome do responsável pela reunião"
                  required
                  className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                />
              </div>

              {/* Data */}
              <div className="space-y-2">
                <Label htmlFor="date" className="text-sm font-medium">
                  Data *
                </Label>
                <Input
                  id="date"
                  data-testid="agendar-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                />
              </div>

              {/* Horários */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTime" className="text-sm font-medium">
                    Hora Início *
                  </Label>
                  <Input
                    id="startTime"
                    data-testid="agendar-start-time-input"
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    required
                    className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endTime" className="text-sm font-medium">
                    Hora Fim *
                  </Label>
                  <Input
                    id="endTime"
                    data-testid="agendar-end-time-input"
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    required
                    className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary font-mono"
                  />
                </div>
              </div>

              {/* Recorrência */}
              <div className="space-y-2">
                <Label htmlFor="recurrence" className="text-sm font-medium">
                  Recorrência
                </Label>
                <select
                  id="recurrence"
                  data-testid="agendar-recurrence-select"
                  value={formData.recurrence}
                  onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                  className="w-full h-12 bg-white/50 backdrop-blur-sm border border-border/50 rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="none">Não repetir</option>
                  <option value="weekly">Semanal</option>
                  <option value="biweekly">Quinzenal</option>
                  <option value="monthly">Mensal</option>
                </select>
              </div>

              {formData.recurrence !== 'none' && (
                <div className="space-y-2">
                  <Label htmlFor="recurrenceEndDate" className="text-sm font-medium">
                    Repetir até *
                  </Label>
                  <Input
                    id="recurrenceEndDate"
                    data-testid="agendar-recurrence-end-date-input"
                    type="date"
                    value={formData.recurrenceEndDate}
                    onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                    min={formData.date}
                    required={formData.recurrence !== 'none'}
                    className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                  />
                  <p className="text-xs text-muted-foreground">
                    A reunião se repetirá {formData.recurrence === 'weekly' ? 'toda semana' : formData.recurrence === 'biweekly' ? 'a cada 2 semanas' : 'todo mês'} até esta data
                  </p>
                </div>
              )}

              <Button
                type="submit"
                data-testid="agendar-submit-button"
                disabled={loading}
                className="w-full h-14 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full text-lg font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                {loading ? 'Agendando...' : 'Agendar Reunião'}
              </Button>
            </form>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-6 md:px-12 py-8 text-center text-sm text-muted-foreground">
        <p>TecnoService Brasil © {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}